/**
 * src/engine/identityAccessSweep.js
 *
 * Identity module — the sweep that turns derived access findings into tasks.
 *
 * WHY THIS IS AN ENGINE PROCESS, AND WHY CLIENT-SIDE (CLAUDE.md's automation
 * engine decision rule, applied per sub-process rather than to the feature):
 *
 *   Does detecting a mismatch between required and held access need a human
 *   present to judge or act? No — it is a comparison of two records. So it is
 *   engine work, not a UI workflow. Deciding what to DO about a finding needs a
 *   human, which is why this sweep only ever raises the task.
 *
 *   May it be skipped while nobody is logged in? For grant gaps and drift, yes:
 *   neither is time-critical, and the finding is also visible on the Role
 *   Matrix the moment an administrator opens it. Raising the task an hour later
 *   costs nothing.
 *
 *   For a LEAVER's standing access, no. Someone who left on Friday should not
 *   keep access until an administrator happens to open a browser tab. That
 *   sub-process genuinely belongs in a server-side Timer Trigger, and it is not
 *   built here — see GH issue linked from docs/automation-engine.md. Doing it
 *   properly means moving the derivation into `azure-functions/shared/*.cjs`
 *   and re-exporting it to the frontend, the way `emailIdentity` already
 *   solves exactly this problem, rather than forking the comparison logic into
 *   a second copy that can disagree with the one on screen.
 *
 * Gated to administrators. The identity module's purpose names the
 * Administrator as the role who governs these definitions, and a task assigned
 * to somebody who cannot action it is worse than no task at all.
 */

import { buildRoleMatrix, GOVERNANCE_STATUS } from '../utils/roleMatrix.js';
import { planIdentityAccessTasks, applyIdentityAccessTasks } from '../utils/identityAccessTasks.js';
import { isGovernanceRbacEnabled } from '../utils/governanceRbacFlag.js';
import { planGovernanceRbacTasks, applyGovernanceRbacTasks } from '../utils/governanceRbacTasks.js';
import { logActivity } from '../api/activityLogger.js';
import {
  readGovernanceRoleDefs,
  readGovernanceRoleAssignments,
} from '../api/governanceRolesApi.js';

export function isIdentitySweepEligible(session) {
  const role = session?.role;
  return role === 'admin' || role === 'super-admin';
}

/**
 * Read the governance catalogue for the sweep, reporting its availability
 * honestly. A failed read is UNAVAILABLE, never an empty list — the difference
 * decides whether governance findings are produced at all (IDN-B-004) and
 * whether their tasks may be withdrawn.
 */
async function readGovernance(state, tenantId) {
  // Absent means ON — only an explicit `false` opts a tenant out (FDNA-01).
  if (!isGovernanceRbacEnabled(state?.features)) {
    return { status: GOVERNANCE_STATUS.DISABLED, defs: [], assignments: [] };
  }
  try {
    const [defs, assignments] = await Promise.all([
      readGovernanceRoleDefs(tenantId),
      readGovernanceRoleAssignments(tenantId),
    ]);
    return { status: GOVERNANCE_STATUS.OK, defs: defs || [], assignments: assignments || [] };
  } catch {
    return { status: GOVERNANCE_STATUS.UNAVAILABLE, defs: [], assignments: [] };
  }
}

/**
 * Run one sweep. Returns a summary; writes only when something changed, so an
 * unchanged tenant does not churn a save every hour.
 *
 * @param {object} ctx
 * @param {object} ctx.state
 * @param {Function} ctx.updateState
 * @param {Array} ctx.users
 * @param {object} ctx.session
 * @param {string} ctx.tenantId
 */
export async function runIdentityAccessSweep({ state, updateState, users, session, tenantId }) {
  if (!state || !isIdentitySweepEligible(session)) {
    return { ran: false, reason: 'not an administrator session' };
  }

  const governance = await readGovernance(state, tenantId);

  const matrix = buildRoleMatrix({
    employees: state.employees || [],
    users: users || [],
    roleDefinitions: state.roleDefinitions || [],
    seasuiteRoles: state.seasuiteRoles || [],
    governance,
  });

  const plan = planIdentityAccessTasks({
    matrix,
    adhocTasks: state.adhocTasks || [],
    assigneeId: session?.userId || null,
    governanceStatus: governance.status,
  });

  // FDNA-01: a required governance role nobody holds, and an opt-out that is
  // due for review (or was never given a reason), are standing conditions
  // with an owner — so they are tasks in the same task list, not banners.
  const govPlan = planGovernanceRbacTasks({
    features: state.features,
    governanceStatus: governance.status,
    assignments: governance.assignments,
    users: users || [],
    adhocTasks: state.adhocTasks || [],
    assigneeId: session?.userId || null,
  });

  const created = plan.toCreate.length + govPlan.toCreate.length;
  const closed = plan.toCloseIds.length + govPlan.toClose.length;
  if (!created && !closed) {
    return { ran: true, created: 0, closed: 0, unchanged: plan.unchanged };
  }

  await updateState((draft) => {
    applyIdentityAccessTasks(draft, plan);
    applyGovernanceRbacTasks(draft, govPlan);
  });

  // The closure is the evidence that the condition was resolved — record it.
  for (const c of govPlan.toClose) {
    logActivity(tenantId, session, {
      module: 'Tasks',
      actionType: 'closed',
      recordId: c.id,
      recordLabel: c.reason,
    });
  }

  return { ran: true, created, closed, unchanged: plan.unchanged };
}
